import { Link } from '@inertiajs/react'

export interface BlogCardPost {
  title: string
  slug: string
  description: string
  category_name: string | null
  category_slug: string | null
  tags: string[]
  published_at: string
  reading_time: string
  featured_image_url: string | null
}

export function formatDate(iso: string): string {
  const d = new Date(iso)
  // timeZone: 'UTC' keeps the rendered date identical on the server and the
  // client — without it, a publish date near midnight UTC formats as a
  // different day in the viewer's timezone, causing a hydration mismatch on
  // the SSR'd blog listings. A publish date is a fixed calendar date anyway,
  // so it shouldn't shift per viewer.
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
}

/** Shared blog post card used by the index, archives and related sections. */
export default function BlogPostCard({ post }: { post: BlogCardPost }) {
  return (
    <Link
      href={`/guides/${post.slug}`}
      className="group flex h-full flex-col overflow-hidden rounded-2xl border border-white/8 bg-white/[0.03] transition-all duration-200 hover:-translate-y-0.5 hover:border-[#ff6a1a]/25 hover:bg-white/[0.05]"
      prefetch
    >
      {post.featured_image_url && (
        <div className="aspect-[16/9] overflow-hidden bg-white/[0.04]">
          <img
            src={post.featured_image_url}
            alt=""
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </div>
      )}
      <div className="flex flex-1 flex-col p-5">
        {post.category_name && (
          <span className="mb-3 inline-block w-fit rounded-full bg-[#ff6a1a]/10 px-2.5 py-1 text-[0.6875rem] font-semibold uppercase tracking-wide text-[#ff6a1a]">
            {post.category_name}
          </span>
        )}
        <h3 className="text-base font-semibold leading-snug text-white transition-colors group-hover:text-[#ff6a1a]">
          {post.title}
        </h3>
        <p className="mt-2 line-clamp-2 flex-1 text-sm leading-relaxed text-white/56">{post.description}</p>
        <div className="mt-4 flex items-center gap-2 text-xs text-white/40">
          <time>{formatDate(post.published_at)}</time>
          <span aria-hidden="true">·</span>
          <span>{post.reading_time}</span>
        </div>
      </div>
    </Link>
  )
}
